import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {Subscription} from "rxjs"
import {onEmit} from "utils/onEmit"
import {IEnvLanguages, IEnvThemeModes, IEnvThemes} from "store/system/environment/env.model"
import {envQuery, envStore} from "store/system/environment/env.store"

export const subscribeEnv = {
  allowsCookies: 'allowsCookies',
  allowsLocalStorage: 'allowsLocalStorage',
  language: 'language',
  theme: 'theme',
  themeMode: 'themeMode',
  formsInline: 'formsInline',
  isMobile: 'isMobile',
}

const EnvironmentService = (subscribe: string[], caller: string) => {

  const subscribed: Subscription[] = []

  const [language, handleLanguage] = useState<IEnvLanguages | undefined>(envQuery.getValue().language)
  const [languageLoading, handleLanguageLoading] = useState<boolean>()
  const [isMobileJs, handleIsMobileJs] = useState<boolean>(envQuery.getValue().isMobileJs)
  const [theme, handleTheme] = useState<IEnvThemes>(envQuery.getValue().theme)
  const [themeMode, handleThemeMode] = useState<IEnvThemeModes>(envQuery.getValue().themeMode)

  const [allowsLocalStorage, handleLocalStorage] = useState<boolean>()
  const [allowsCookies, handleCookies] = useState<boolean>()
  const [isMobileWidth, handleIsMobileWidth] = useState<boolean>()
  const [formsInline, setFormsInline] = useState<boolean>(true)

  useEffect(() => {
    if (!subscribe) throw Error(`Service.Env.${caller}: Define what you want to subscribe to`)
    if (!caller) throw Error(`Service.Env.${caller}: Define where you are calling from`)

    subscribe.map(s => {
      if (!Object.keys(subscribeEnv).includes(s))
        throw new Error(`Service.Env.${caller}: Can't subscribe to: ${s}`)
    })

    Logger(`Service.Env.init.${caller}`, subscribe.join(', '))

    if (subscribe.includes(subscribeEnv.allowsCookies))
      subCookies()

    if (subscribe.includes(subscribeEnv.allowsLocalStorage))
      subLocalStorage()

    if (subscribe.includes(subscribeEnv.language))
      subLanguage()

    if (subscribe.includes(subscribeEnv.theme))
      subTheme()

    if (subscribe.includes(subscribeEnv.themeMode))
      subThemeMode()

    if (subscribe.includes(subscribeEnv.formsInline))
      subFormsInline()

    if (subscribe.includes(subscribeEnv.isMobile))
      subIsMobile()

    return () => {
      Logger(`Service.Env.unloading.${caller}`)
      subscribed.map(s => s.unsubscribe())
    }

  }, [])

  // When initially called, and we don't know the value from LocalStorage, we should check the browser preference
  // for either Light or Dark mode
  useEffect(() => {
    if (themeMode !== undefined) return
    if (!subscribe.includes(subscribeEnv.themeMode)) return
    const newMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    envStore.update(state => {
      state.themeMode = newMode
    })
  }, [themeMode])

  const toggleThemeMode = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light'
    envStore.update(state => {
      state.themeMode = newMode
    })
  }

  const setLanguage = (lng: IEnvLanguages) => {
    if (!lng) return

    let language: IEnvLanguages = lng
    envStore.update(state => {
      state.language = language
    })
  }

  return {
    allowsLocalStorage,
    allowsCookies,
    language,
    languageLoading,
    theme,
    themeMode,
    toggleThemeMode,
    setLanguage,
    formsInline,
    isMobileJs,
    isMobileWidth,
  }

  function subCookies() {
    subscribed.push(onEmit(envQuery.select('allowsCookies'), newAllowsCookies => {
      handleCookies(newAllowsCookies)
    }))
  }

  function subLocalStorage() {
    subscribed.push(onEmit(envQuery.select('allowsLocalStorage'), newLs => {
      handleLocalStorage(newLs)
    }))
  }

  function subLanguage() {
    subscribed.push(onEmit(envQuery.select('language'), newLanguage => {
      handleLanguage(newLanguage)
    }))
    subscribed.push(onEmit(envQuery.select('languageLoading'), newLoading => {
      handleLanguageLoading(newLoading)
    }))
  }

  function subTheme() {
    subscribed.push(onEmit(envQuery.select('theme'), newTheme => {
      handleTheme(newTheme)
    }))
  }

  function subThemeMode() {
    subscribed.push(onEmit(envQuery.select('themeMode'), newThemeMode => {
      handleThemeMode(newThemeMode)
    }))
  }

  function subFormsInline() {
    subscribed.push(onEmit<boolean>(envQuery.select('formsInline'), newFormsInline => {
      setFormsInline(newFormsInline)
    }))
  }

  function subIsMobile() {
    subscribed.push(onEmit(envQuery.select('isMobileJs'), newIsMobileJs => {
      handleIsMobileJs(newIsMobileJs)
    }))
    subscribed.push(onEmit(envQuery.select('isMobileWidth'), newIsMobileWidth => {
      handleIsMobileWidth(newIsMobileWidth)
    }))
  }
}

export default EnvironmentService
